import React from 'react'

import { Box, Button, InputCheckBox, Text } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCandidates } from 'modules/ats/hooks/candidates/useCandidates'
import { useDeleteTag } from 'modules/ats/hooks/tags/useDeleteTag'
import type { Tag } from 'modules/ats/hooks/tags/useTags'

interface Props {
  tag: Tag
  onClose: () => void
  onLoad: (loading: boolean) => void
}

export const DeleteTagModal = ({ tag, onClose, onLoad }: Props) => {
  const [isChecked, setIsChecked] = React.useState(false)
  const { loading, candidates } = useCandidates({ variables: { atsTagIds: [tag.id] } })

  const isTagBeingUsed = !loading && !!candidates.length

  const modalTitle = isTagBeingUsed
    ? i18n.t('ats.tags.delete.confirm.title.assigned')
    : i18n.t('ats.tags.delete.confirm.title.unassigned')

  const modalDescription = isTagBeingUsed
    ? i18n.t('ats.tags.delete.confirm.description.assigned')
    : i18n.t('ats.tags.delete.confirm.description.unassigned')

  useUnsafeEffect(() => onLoad(loading), [loading, onLoad])

  const { loading: deleting, deleteTag } = useDeleteTag(tag, {
    onError(error) {
      app.setFlash({ type: 'wrong', message: error.message, time: 2000 })
    },
    onCompleted() {
      app.setFlash({ type: 'correct', message: i18n.t('common.removed_successfully'), time: 2000 })
    },
  })

  if (loading) return null

  return (
    <Modal
      illustration="delete"
      title={modalTitle}
      description={modalDescription}
      onClose={onClose}
      onSubmit={() => deleteTag()}
      footer={
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <Button type="secondary" disabled={deleting} onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>

          <Button type="negative" disabled={deleting || (!isChecked && isTagBeingUsed)} submit>
            {i18n.t('ats.tags.delete.confirm.button')}
          </Button>
        </Box>
      }
    >
      {isTagBeingUsed && (
        <Box gap="s24">
          {isTagBeingUsed && (
            <Box flexDirection="row" gap="s8" alignItems="center">
              <InputCheckBox checked={isChecked} onClick={() => setIsChecked(!isChecked)} />
              <Text.Normal>{i18n.t('ats.tags.delete.confirm.checkbox')}</Text.Normal>
            </Box>
          )}
        </Box>
      )}
    </Modal>
  )
}
